import delay from './delay';
import { FetchGraphQLDataParamTypes } from './utils.types';

const CONTENTFUL_PROXY_ENDPOINT = '/api/contentful/proxy';

/** Client-safe fetch via the Contentful proxy API route (no tokens in bundle). */
export default async function fetchContentfulProxy<T>({
  method = 'POST',
  query,
  variables,
}: Omit<FetchGraphQLDataParamTypes, 'endpoint' | 'cache'>): Promise<T> {
  const headers = {
    'Content-Type': 'application/json',
  };

  const maxRetries = 5;
  const retryDelay = 5000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const body = JSON.stringify(variables ? { query, variables } : { query, variables });

      const response = await fetch(CONTENTFUL_PROXY_ENDPOINT, {
        cache: 'no-store',
        method,
        headers,
        body,
      });

      if (!response?.ok) {
        let errorDetails;
        try {
          const errorJson = await response.json();
          errorDetails = JSON.stringify(errorJson, null, 4);
        } catch {
          errorDetails = await response.text();
        }

        if (response.status === 429 && attempt < maxRetries) {
          // eslint-disable-next-line no-console
          console.warn(
            `Rate limit exceeded. Retrying in ${retryDelay / 1000} seconds... (Attempt ${attempt}/${maxRetries})`,
          );
          await delay({ ms: retryDelay });
          continue;
        }

        throw new Error(
          `Network response was not ok: ${response?.status} ${response?.statusText}\nDetails: ${errorDetails}`,
        );
      }

      const data = await response.json();

      if (!data || !data?.data) {
        throw new Error('Response JSON does not contain data.');
      }

      return data?.data as T;
    } catch (error) {
      if (attempt === maxRetries) {
        if (error instanceof Error) {
          throw new Error(`Fetching data failed after ${maxRetries} attempts: ${error?.message}`);
        }
        throw new Error('An unknown error occurred while fetching data.');
      }
      await delay({ ms: retryDelay });
    }
  }

  throw new Error('Unexpected error in fetchContentfulProxy.');
}
